import { registerComponent } from "./component";
import { getRootUrl } from "./context";
import { createElement, onEvent } from "./_dom";

function getSel(field) {
	const start = field.selectionStart;
	const end = field.selectionEnd;
	const text = field.value.substring(start, end);
	return { start, end, text };
}

function setSel(field, sel) {
	field.selectionStart = sel.start;
	field.selectionEnd = sel.end;
}

function replaceSel(field, sel) {
	field.value = field.value.substring(0, sel.start) + sel.text + field.value.substring(sel.end);
}

function processLines(field, fn, count) {
	const sel = getSel(field);

	var value = field.value;

	var n;
	sel.start = 1 + value.lastIndexOf("\n", sel.start);
	n = value.indexOf("\n", sel.end);
	sel.end = n >= 0 ? n : value.length;

	var lines = value.substring(sel.start, sel.end).split("\n");
	if (!count || count < 1 || count > lines.length) count = lines.length;
	for (var i = 0; i < count; i++)
		lines[i] = fn(lines[i]);

	sel.text = lines.join("\n");
	replaceSel(field, sel);
	return sel;
}

const functions = {
	indent: (field, value) => {
		const count = /^#{1,3}$/.test(value) ? 1 : 0;
		const sel = processLines(field, line => `${value || '\t'}${line}`, count);
		if (value == null) {
			sel.end = sel.start + sel.text.length;
		} else {
			sel.end = sel.start;
		}
		setSel(field, sel);
	},
	unindent: (field, value) => {
		var sel = processLines(field, line => line.replace(value, ""));
		sel.end = sel.start + sel.text.length;
		setSel(field, sel);
	},
	insert: (field, value) => {
		var sel = getSel(field);
		sel.text = value;
		replaceSel(field, sel);
	},
	wrap: (field, value) => {
		let [prefix, suffix] = value.split('", "');
		suffix = suffix ?? prefix;
		var sel = getSel(field);
		sel.text = prefix + sel.text + suffix;
		replaceSel(field, sel);
		setSel(field, { start: sel.start + prefix.length, end: sel.start + prefix.length });
		field.focus();
	},
	list: (field/*, value*/) => {
		let n = 0;
		const sel = processLines(field, line => `${++n}. ${line}`);
		sel.end = sel.start;
		setSel(field, sel);
	}
};

const toolbarObj = [
	{ href: 'h1', tooltip: 'Header 1', name: 'header-1', symbols: '# ', type: 'indent' },
	{ href: 'h2', tooltip: 'Header 2', name: 'header-2', symbols: '## ', type: 'indent' },
	{ href: 'h3', tooltip: 'Header 3', name: 'header-3', symbols: '### ', type: 'indent' },
	{ href: 'strong', tooltip: 'Bold', name: 'bold', symbols: '**', type: 'wrap' },
	{ href: 'em', tooltip: 'Italic', name: 'italic', symbols: '_', type: 'wrap' },
	{ href: 'code', tooltip: 'Code', name: 'code', symbols: '``', type: 'wrap' },
	{ href: 'ol', tooltip: 'Numbered List', name: 'numbered-list', symbols: '', type: 'list' },
	{ href: 'ul', tooltip: 'Bulleted List', name: 'bulleted-list', symbols: '* ', type: 'indent' },
	{ href: 'blockquote', tooltip: 'Block Quote', name: 'block-quote', symbols: '> ', type: 'indent' },
	{ href: 'indent', tooltip: 'Indent', name: 'indent', symbols: '', type: 'indent' },
	{ href: 'unindent', tooltip: 'Unindent', name: 'unindent', symbols: /^(\t|\x20{1,9})/g, type: 'unindent' },
	{ href: 'hr', tooltip: 'Horizontal Line', name: 'hr', symbols: '\r\n* * *\r\n', type: 'insert' },
	{ href: 'a', tooltip: 'Web Link', name: 'a', symbols: '[Link Label](", ")', type: 'wrap' },
	{ href: 'img', tooltip: 'Web Image', name: 'img', symbols: '![Image Label](", ")', type: 'wrap' },
];


function createMarkupEditor(field) {
	const rootUrl = getRootUrl(field);
	const toolbarObjArr = toolbarObj.map(item =>
		createElement('button', {
				type: "button",
				value: `${item.href}`,
				title: `${item.tooltip}`,
				class: `v3-markup-toolbar__button v3-button ui-icon-only ui-icon-${item.name}`
			},
			createElement("span", { class: "ui-icontext" }, item.tooltip)));
	toolbarObjArr.push(
		createElement("button", { type: "button", value: "preview", title: 'Preview', class: 'v3-markup-toolbar__button v3-button ui-icon-only ui-icon-magnifying-glass js-markup-preview' }, createElement("span", { class: "ui-icontext" }, "Preview")),
		createElement("a", { href: `${rootUrl}/html/markuphelp.htm`, target: "_markuphelp", title: 'Help', class: 'v3-markup-toolbar__button v3-button ui-icon-only ui-icon-circle-question' }, createElement("span", { class: "ui-icontext" }, "Help"))
	);
	const toolbar = createElement('div', { class: "v3-markup-toolbar" }, ...toolbarObjArr);
	onEvent(toolbar, '.v3-markup-toolbar__button', "click", (e, item) => {
		e.preventDefault();
		if (item.value === "preview") {
			let action = "historytext.aspx";
			if (/\/setup\//.test(location.pathname)) {
				action = "../" + action;
			} else if (field.getAttribute('name')) {
				const id = field.getAttribute('name').match(/^f_(\d+)(_o)?$/);
				null !== id && (action += "?fid=" + id[1]);
			}
			const form = createElement('form', { action, method: "post", target: "preview" },
				createElement('input', { type: 'hidden', name: 'text', value: `${field.value}` })
			);
			document.body.appendChild(form);
			form.submit();
			form.remove();
		} else {
			const href = item.value;
			const { type, symbols } = toolbarObj.find(item => item.href === href);
			const fn = functions[type];
			if (fn) {
				fn(field, symbols);
				field.focus();
			}
		}
	});
	field.before(toolbar);
}

const markupeditor = registerComponent(createMarkupEditor);

function initMarkupEditor(context, selector = "textarea.js-markupeditor") {
	return markupeditor(selector, context);
}

function initMarkupPreview(context, selector = "div.markup > div.whois") {
	(context || document).querySelectorAll(selector).forEach(whois => {
		const text = whois.innerText;
		whois.innerText = text.replace(/^\[(.*)\]$/, "$1");
	});
}

function init(context) {
	initMarkupEditor(context);
	initMarkupPreview(context);
}

export { markupeditor, init };